import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import {
  getCliente,
  getAgendamentosParaCobrar,
  criarCobranca,
  getConfiguracoes,
  emitirFaturaParcelas,
  updateEstadoAgendamento,
  descarregarFaturaPdf,
} from '../../lib/supabase'
import { valoresAgendamento, estadoParcelas, parcelasPorCobrar, parcelasSugeridas, parcelasPorFaturar, fmtEur, labelMetodo, parseMetodosPagamento, METODO_STRIPE } from '../../lib/valores'
import { ESTADOS, ORDEM_ESTADOS } from '../../lib/estados'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'
import { useResponsive } from '../../hooks/useResponsive'

const valInput = { width: '100%', padding: '9px 12px', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', fontSize: '14px', fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)', color: 'var(--cor-texto)' }

// Sinal esperado de uma marcação (para pré-preencher a parcela ainda inexistente):
// valor fixo do serviço ou percentagem global do preço. Mesma regra da Nova Marcação.
function sinalEsperado(ag, config) {
  const svc = ag?.servicos
  if (svc?.sinal_valor != null && svc.sinal_valor !== '') return Number(svc.sinal_valor)
  const pct = parseFloat(config?.stripe_sinal_percentagem)
  if (!Number.isFinite(pct)) return 0
  const base = ag?.preco_total != null ? Number(ag.preco_total) : Number(svc?.preco || 0)
  return Math.round(base * pct) / 100
}

// Uma parcela (sinal ou restante) que ainda está por pagar → cobrável.
function parcelaCobravel(p) { return p && !p.pago }

// Chave de seleção de uma parcela. A seleção é por PARCELA, não por marcação:
// o sinal pode ser cobrado hoje e o restante só depois do serviço.
const chaveParcela = (agId, tipo) => `${agId}:${tipo}`

const tiposCobraveis = parcelasPorCobrar

// Todas as parcelas pagas e ainda sem documento vêm pré-escolhidas — já são
// dinheiro recebido à espera de fatura, seja qual for o meio de pagamento.
// Desmarcar é sempre possível.
function idsPorFaturar(linhas) {
  const ids = []
  for (const l of linhas) {
    for (const t of parcelasPorFaturar(l)) {
      const p = l[t]
      if (p?.id) ids.push(p.id)
    }
  }
  return ids
}

// Chaves de seleção do sinal por pagar de TODAS as marcações, passado ou
// futuro — é o valor que segura a marcação, por isso vem sempre marcado.
// (O restante continua a só se sugerir na marcação vinda do detalhe: ver
// `parcelasSugeridas`.)
function chavesSinalPorPagar(linhas) {
  return linhas.filter(l => parcelaCobravel(l.sinal)).map(l => chaveParcela(l.ag.id, 'sinal'))
}

export default function Cobrar() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const preSelId = searchParams.get('agendamento') // marcação vinda do detalhe
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { perfil } = useAuth()
  const toast = useToast()
  const { isDesktop } = useResponsive()

  const [carregando, setCarregando] = useState(true)
  const [cliente, setCliente] = useState(null)
  const [config, setConfig] = useState({})
  const [linhas, setLinhas] = useState([]) // { ag, futuro, sinal:{...}, restante:{...} }
  // Seleção ao nível da PARCELA: chaves `agId:sinal` / `agId:restante`. Permite
  // cobrar só o sinal, só o restante, ou ambos — e misturar entre marcações.
  const [sel, setSel] = useState(() => new Set())
  const [editValores, setEditValores] = useState({}) // { 'agId-sinal': valor, ... }
  const [metodo, setMetodo] = useState('dinheiro')
  const [enviando, setEnviando] = useState(false)

  // Fase 2 (fatura). Chega-se aqui depois de marcar como pago, ou direto por
  // `?modo=fatura` — é o que permite faturar mais tarde, faturar um sinal do
  // Stripe, ou reemitir depois de cancelar uma fatura.
  const [fase, setFase] = useState(() => (searchParams.get('modo') === 'fatura' ? 'fatura' : 'cobrar'))
  const [cobranca, setCobranca] = useState(null) // { n, total, agId }
  const [emitindo, setEmitindo] = useState(false)
  // Seleção do modo fatura: ids de PARCELAS (pagamentos.id).
  const [selFat, setSelFat] = useState(() => new Set())
  // Estado a aplicar às marcações NÃO FUTURAS ao emitir a fatura. Null =
  // ainda não houve escolha explícita nesta sessão (usa-se 'concluido' por
  // defeito ao emitir, ou o estado comum das marcações selecionadas, se só
  // houver um). Uma escolha explícita (confirmada) tem sempre prioridade.
  const [estadoEscolhido, setEstadoEscolhido] = useState(null)
  const [estadoPendente, setEstadoPendente] = useState(null) // à espera de confirmação
  const [guardandoEstado, setGuardandoEstado] = useState(false)
  // Depois de emitir com sucesso: ecrã final com o PDF e o atalho para a
  // agenda, em vez de navegar logo para fora. { id, numero, tipo, repartido }
  const [faturaEmitida, setFaturaEmitida] = useState(null)
  const [obtendoPdf, setObtendoPdf] = useState(false)

  const perfilNome = perfil?.nome || perfil?.email || 'backoffice'
  const vendusAtivo = config?.vendus_ativo === 'true'
  const metodosPagamento = useMemo(() => parseMetodosPagamento(config), [config])

  // Garante que o método selecionado existe na lista do tenant (o defeito
  // 'dinheiro' pode não estar configurado). Assim que a lista carrega, se o
  // método atual não existir, passa para o primeiro disponível.
  useEffect(() => {
    if (metodosPagamento.length > 0 && !metodosPagamento.some(m => m.chave === metodo)) {
      setMetodo(metodosPagamento[0].chave)
    }
  }, [metodosPagamento, metodo])

  useEffect(() => {
    if (!tenant || !id) return
    setCarregando(true)
    Promise.all([
      getCliente(tenant.id, id),
      getAgendamentosParaCobrar(tenant.id, id),
      getConfiguracoes(tenant.id),
    ])
      .then(([cli, { agendamentos, pagamentos }, cfg]) => {
        if (!cli) { navigate('/admin/clientes', { replace: true }); return }
        setCliente(cli)
        setConfig(cfg || {})
        const agora = Date.now()
        const ls = agendamentos.map(ag => {
          const futuro = new Date(ag.data_hora_inicio).getTime() > agora
          const est = estadoParcelas(ag, pagamentos, { sinalEsperado: sinalEsperado(ag, cfg) })
          return { ag, futuro, sinal: est.sinal, restante: est.restante }
        })
        setLinhas(ls)
        // Sinal por pagar vem sempre marcado, em TODAS as marcações — é o
        // valor que segura a marcação, não faz sentido ficar por escolher.
        const inicial = new Set(chavesSinalPorPagar(ls))
        // A marcação vinda do detalhe ganha também o resto do sugerido
        // (o restante, se não for futura).
        if (preSelId) {
          const l = ls.find(x => x.ag.id === preSelId)
          if (l) for (const t of parcelasSugeridas(l)) inicial.add(chaveParcela(preSelId, t))
        }
        setSel(inicial)
        // Modo fatura direto: vem tudo o que está pago e por faturar já escolhido.
        if (searchParams.get('modo') === 'fatura') {
          setSelFat(new Set(idsPorFaturar(ls)))
        }
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, id])

  // Relê as parcelas da BD. Necessário depois de cobrar: as parcelas acabadas
  // de criar só têm id do lado do servidor, e é por id que se fatura.
  async function recarregarLinhas() {
    const { agendamentos, pagamentos } = await getAgendamentosParaCobrar(tenant.id, id)
    const agora = Date.now()
    const ls = agendamentos.map(ag => {
      const futuro = new Date(ag.data_hora_inicio).getTime() > agora
      const est = estadoParcelas(ag, pagamentos, { sinalEsperado: sinalEsperado(ag, config) })
      return { ag, futuro, sinal: est.sinal, restante: est.restante }
    })
    setLinhas(ls)
    return ls
  }

  // O que é cobrável por linha: sinal por pagar sempre; restante por pagar só nos
  // passados (nos futuros o restante fica "a definir" — só depois do serviço).
  function sinalCobravel(l) { return parcelaCobravel(l.sinal) }
  function restanteCobravel(l) { return parcelaCobravel(l.restante) }
  function temAlgoACobrar(l) { return tiposCobraveis(l).length > 0 }

  // Valor efetivo (editado ou sugerido) de uma parcela por pagar.
  function valorParcela(agId, tipo, parcela) {
    const k = `${agId}-${tipo}`
    return k in editValores ? editValores[k] : (parcela.valorSugerido || 0)
  }
  function setValorParcela(agId, tipo, raw) {
    const n = Number(String(raw).replace(',', '.'))
    setEditValores(prev => ({ ...prev, [`${agId}-${tipo}`]: Number.isFinite(n) && n >= 0 ? n : 0 }))
  }

  function toggleParcela(agId, tipo) {
    setSel(prev => {
      const n = new Set(prev)
      const k = chaveParcela(agId, tipo)
      if (n.has(k)) n.delete(k); else n.add(k)
      return n
    })
  }

  // Checkbox da marcação: liga/desliga de uma vez as parcelas cobráveis dela.
  // Marcada quando estão todas; "meia" (indeterminate) quando só algumas.
  function estadoAgendamento(l) {
    const ts = tiposCobraveis(l)
    if (ts.length === 0) return { todas: false, algumas: false }
    const n = ts.filter(t => sel.has(chaveParcela(l.ag.id, t))).length
    return { todas: n === ts.length, algumas: n > 0 && n < ts.length }
  }
  function toggleAgendamento(l) {
    const { todas } = estadoAgendamento(l)
    setSel(prev => {
      const n = new Set(prev)
      for (const t of tiposCobraveis(l)) {
        const k = chaveParcela(l.ag.id, t)
        if (todas) n.delete(k); else n.add(k)
      }
      return n
    })
  }

  // O que "Selecionar tudo o que falta" abrange: o sugerido de cada marcação
  // (sem o restante das futuras — esse escolhe-se à mão).
  const alvos = useMemo(
    () => linhas.flatMap(l => parcelasSugeridas(l).map(t => chaveParcela(l.ag.id, t))),
    [linhas]
  )
  function toggleTodos() {
    const todos = alvos.length > 0 && alvos.every(k => sel.has(k))
    // Acrescenta em vez de substituir, para não desfazer um restante futuro
    // que tenha sido marcado de propósito.
    setSel(todos ? new Set() : new Set([...sel, ...alvos]))
  }

  // Resumo do que está selecionado.
  const resumo = useMemo(() => {
    let nS = 0, vS = 0, nR = 0, vR = 0
    for (const l of linhas) {
      if (sinalCobravel(l) && sel.has(chaveParcela(l.ag.id, 'sinal'))) {
        nS++; vS += Number(valorParcela(l.ag.id, 'sinal', l.sinal)) || 0
      }
      if (restanteCobravel(l) && sel.has(chaveParcela(l.ag.id, 'restante'))) {
        nR++; vR += Number(valorParcela(l.ag.id, 'restante', l.restante)) || 0
      }
    }
    return { nS, vS, nR, vR, total: vS + vR }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhas, sel, editValores])

  async function cobrar() {
    if (resumo.total <= 0) return
    setEnviando(true)
    try {
      const itens = []
      for (const l of linhas) {
        const nome = l.ag.servicos?.nome || 'Serviço'
        if (sinalCobravel(l) && sel.has(chaveParcela(l.ag.id, 'sinal'))) {
          const v = Number(valorParcela(l.ag.id, 'sinal', l.sinal))
          if (v > 0) itens.push({ agendamento_id: l.ag.id, tipo: 'sinal', descricao: `Sinal - ${nome}`, valor: Number(v.toFixed(2)) })
        }
        if (restanteCobravel(l) && sel.has(chaveParcela(l.ag.id, 'restante'))) {
          const v = Number(valorParcela(l.ag.id, 'restante', l.restante))
          if (v > 0) itens.push({ agendamento_id: l.ag.id, tipo: 'restante', descricao: nome, valor: Number(v.toFixed(2)) })
        }
      }
      if (itens.length === 0) { toast.erro('Nada selecionado para cobrar.'); setEnviando(false); return }
      const cobrancaId = await criarCobranca(tenant.id, {
        clienteId: cliente.id,
        clienteNome: cliente.nome_amigavel || cliente.nome,
        metodo,
        itens,
      }, perfilNome)
      toast.sucesso(`${itens.length} ${itens.length === 1 ? 'parcela marcada' : 'parcelas marcadas'} como paga${itens.length === 1 ? '' : 's'} (${labelMetodo(metodo)}).`)
      // Marcação a que voltamos no fim: a que veio do detalhe (se estiver na
      // cobrança), senão a primeira marcação cobrada.
      const idsCobrados = itens.map(i => i.agendamento_id)
      const agId = (preSelId && idsCobrados.includes(preSelId)) ? preSelId : idsCobrados[0]
      // Passa ao modo fatura com tudo o que está pago e por faturar já
      // escolhido — o que acabou de ser cobrado agora, mais qualquer outro
      // pagamento pendente de faturar do mesmo cliente.
      const ls = await recarregarLinhas()
      setSelFat(new Set(idsPorFaturar(ls)))
      setSel(new Set())
      setCobranca({ id: cobrancaId, n: itens.length, total: resumo.total, agId })
      setFase('fatura')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setEnviando(false)
    }
  }

  async function emitirFatura() {
    const ids = [...selFat]
    if (ids.length === 0) return
    setEmitindo(true)
    try {
      const r = await emitirFaturaParcelas(tenant.id, ids)

      // Marcações NÃO futuras cobertas por este documento passam a
      // Concluído — o serviço já aconteceu e está pago e faturado. Se o
      // operador já tinha escolhido outro estado explicitamente (com
      // confirmação, na caixa "Estado da marcação"), respeita-se essa
      // escolha em vez de forçar Concluído.
      const alvoEstado = estadoEscolhido || 'concluido'
      const agsAMudar = linhas.filter(l =>
        !l.futuro &&
        (selFat.has(l.sinal?.id) || selFat.has(l.restante?.id)) &&
        l.ag.estado !== alvoEstado
      )
      let nMudados = 0
      if (agsAMudar.length > 0) {
        await Promise.all(agsAMudar.map(l => updateEstadoAgendamento(tenant.id, l.ag.id, alvoEstado, perfilNome)))
        nMudados = agsAMudar.length
      }

      // Fica no ecrã de sucesso (PDF + atalho para a agenda) em vez de
      // navegar logo para fora — dá tempo de obter o documento ali mesmo.
      setFaturaEmitida({
        id: r?.id, numero: r?.numero, tipo: r?.tipo,
        repartido: !!r?.repartido, nMudados, alvoEstado,
      })
    } catch (e) {
      toast.erro(`Os pagamentos ficaram registados, mas a fatura falhou: ${e.message}`)
    } finally {
      setEmitindo(false)
    }
  }

  async function obterPdfFaturaEmitida() {
    if (!faturaEmitida?.id) return
    setObtendoPdf(true)
    try {
      const { url } = await descarregarFaturaPdf(tenant.id, { faturaId: faturaEmitida.id })
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setObtendoPdf(false)
    }
  }

  // Volta ao detalhe da marcação cobrada (onde se vê a fatura e se obtém o PDF).
  // Se por algum motivo não soubermos a marcação, cai na ficha do cliente.
  function voltarAoDetalhe() {
    if (cobranca?.agId) navigate(`/admin/agendamento/${cobranca.agId}`)
    else navigate(`/admin/clientes/${cliente.id}`)
  }

  if (carregando) return <div style={{ padding: '48px 24px' }}><Loading /></div>

  const nomeCliente = cliente?.nome_amigavel || cliente?.nome || 'Cliente'
  // Só mostramos marcações com algo por cobrar; as totalmente saldadas saem da
  // lista (contamos quantas ficaram de fora, para dar contexto).
  const linhasVisiveis = linhas.filter(temAlgoACobrar)
  const nadaParaCobrar = linhasVisiveis.length === 0

  // ─── Fase 2: emitir fatura ────────────────────────────────
  // Mesmo layout da fase 1 (cobrar): painel "Marcações" com checkbox por
  // marcação (liga as duas parcelas) + caixa própria por parcela, e o resumo
  // ao lado/em baixo. Só que aqui as parcelas já estão pagas — o que se
  // escolhe é o que entra no DOCUMENTO, não o que se cobra.
  if (fase === 'fatura') {
    // Já emitida: ecrã final, sem mais nada por escolher.
    if (faturaEmitida) {
      const tipoLabel = { FS: 'Fatura simplificada', FR: 'Fatura-recibo' }[faturaEmitida.tipo] || 'Fatura'
      return (
        <div style={{ maxWidth: '560px', margin: '0 auto', padding: '64px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: '44px', marginBottom: '8px' }}>✅</p>
          <h2 style={{ fontSize: '21px', marginBottom: '6px' }}>Fatura emitida com sucesso</h2>
          <p style={{ fontSize: '14px', color: 'var(--cor-texto-suave)', margin: '0 0 4px' }}>
            {tipoLabel} {faturaEmitida.numero || faturaEmitida.id} · {nomeCliente}
          </p>
          {faturaEmitida.repartido && (
            <p style={{ fontSize: '12.5px', color: 'var(--cor-texto-suave)', margin: '0 0 4px' }}>
              Pagamento repartido pelos meios usados.
            </p>
          )}
          {faturaEmitida.nMudados > 0 && (
            <p style={{ fontSize: '12.5px', color: 'var(--cor-texto-suave)', margin: 0 }}>
              {faturaEmitida.nMudados} marcaç{faturaEmitida.nMudados === 1 ? 'ão passou' : 'ões passaram'} a {ESTADOS[faturaEmitida.alvoEstado]?.label || faturaEmitida.alvoEstado}.
            </p>
          )}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '28px' }}>
            <button onClick={obterPdfFaturaEmitida} disabled={obtendoPdf} className="btn-primario" style={{ opacity: obtendoPdf ? 0.6 : 1 }}>
              {obtendoPdf ? 'A obter…' : 'Obter PDF'}
            </button>
            <button onClick={() => navigate('/admin')} className="btn-secundario">
              Ir para a Agenda do Dia
            </button>
          </div>
        </div>
      )
    }

    const linhasFat = linhas.filter(l => parcelasPorFaturar(l).length > 0)
    const nadaParaFaturar = linhasFat.length === 0

    function estadoAgendamentoFat(l) {
      const ts = parcelasPorFaturar(l)
      if (ts.length === 0) return { todas: false, algumas: false }
      const n = ts.filter(t => selFat.has(l[t]?.id)).length
      return { todas: n === ts.length, algumas: n > 0 && n < ts.length }
    }
    function toggleAgendamentoFat(l) {
      const { todas } = estadoAgendamentoFat(l)
      setSelFat(prev => {
        const n = new Set(prev)
        for (const t of parcelasPorFaturar(l)) {
          const id = l[t]?.id
          if (!id) continue
          if (todas) n.delete(id); else n.add(id)
        }
        return n
      })
    }
    function toggleParcelaFat(id) {
      setSelFat(prev => {
        const n = new Set(prev)
        if (n.has(id)) n.delete(id); else n.add(id)
        return n
      })
    }
    const alvosFat = linhasFat.flatMap(l => parcelasPorFaturar(l).map(t => l[t]?.id).filter(Boolean))
    function toggleTodosFat() {
      const todos = alvosFat.length > 0 && alvosFat.every(id => selFat.has(id))
      setSelFat(todos ? new Set() : new Set(alvosFat))
    }

    // Resumo: mesma forma que "Sinais/Restantes/total" da fase 1.
    const resumoFat = { nS: 0, vS: 0, nR: 0, vR: 0 }
    const meiosFat = new Set()
    for (const l of linhasFat) {
      for (const t of parcelasPorFaturar(l)) {
        const p = l[t]
        if (!p?.id || !selFat.has(p.id)) continue
        if (t === 'sinal') { resumoFat.nS++; resumoFat.vS += Number(p.valor || 0) }
        else { resumoFat.nR++; resumoFat.vR += Number(p.valor || 0) }
        meiosFat.add(p.origem === 'stripe' ? METODO_STRIPE : (p.metodo || 'outro'))
      }
    }
    const totalFat = resumoFat.vS + resumoFat.vR

    // Marcações NÃO futuras cobertas pela seleção atual — é sobre estas que a
    // caixa "Estado da marcação" abaixo actua. As futuras não entram: o
    // serviço ainda não aconteceu, não faz sentido oferecer "Concluído".
    const agsNaoFuturos = linhasFat.filter(l =>
      !l.futuro && (selFat.has(l.sinal?.id) || selFat.has(l.restante?.id))
    )
    const estadosDistintos = new Set(agsNaoFuturos.map(l => l.ag.estado))
    // Se todas partilham o mesmo estado, é esse que aparece selecionado; se
    // uma escolha explícita já foi confirmada nesta sessão, essa ganha.
    const estadoComum = estadosDistintos.size === 1 ? [...estadosDistintos][0] : null
    const estadoAtivo = estadoEscolhido || estadoComum

    function clicarEstado(novo) {
      if (guardandoEstado || agsNaoFuturos.length === 0 || novo === estadoAtivo) return
      setEstadoPendente(novo)
    }

    async function confirmarMudancaEstado() {
      const novo = estadoPendente
      if (!novo || agsNaoFuturos.length === 0) return
      setGuardandoEstado(true)
      try {
        await Promise.all(agsNaoFuturos.map(l => updateEstadoAgendamento(tenant.id, l.ag.id, novo, perfilNome)))
        const idsAlvo = new Set(agsNaoFuturos.map(l => l.ag.id))
        setLinhas(prev => prev.map(l => idsAlvo.has(l.ag.id) ? { ...l, ag: { ...l.ag, estado: novo } } : l))
        setEstadoEscolhido(novo)
        setEstadoPendente(null)
        toast.sucesso(`Estado atualizado em ${agsNaoFuturos.length} marcaç${agsNaoFuturos.length === 1 ? 'ão' : 'ões'}.`)
      } catch (e) {
        toast.erro(e.message)
      } finally {
        setGuardandoEstado(false)
      }
    }

    // Caixa "Estado da marcação" — mesmo padrão do detalhe da marcação. Só
    // aparece quando a seleção atual cobre alguma marcação não futura.
    const estadoBox = agsNaoFuturos.length > 0 && (
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px 20px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Estado da marcação
        </div>
        <p style={{ fontSize: '11.5px', color: 'var(--cor-texto-suave)', margin: '0 0 12px', lineHeight: 1.4 }}>
          Aplica-se às {agsNaoFuturos.length} marcaç{agsNaoFuturos.length === 1 ? 'ão selecionada que já aconteceu' : 'ões selecionadas que já aconteceram'}.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
          {ORDEM_ESTADOS.map(e => {
            const cfg = ESTADOS[e]
            const ativo = estadoAtivo === e
            return (
              <button
                key={e}
                onClick={() => clicarEstado(e)}
                disabled={guardandoEstado}
                style={{
                  padding: '10px 12px', borderRadius: 'var(--raio)', fontSize: '13.5px', fontWeight: '600',
                  cursor: guardandoEstado ? 'default' : 'pointer',
                  border: `1.5px solid ${ativo ? cfg.cor : 'var(--cor-borda)'}`,
                  background: ativo ? cfg.cor : 'var(--cor-branco)',
                  color: ativo ? '#fff' : cfg.cor,
                  opacity: guardandoEstado && !ativo ? 0.5 : 1,
                }}
              >
                {cfg.label}
              </button>
            )
          })}
        </div>

        {estadoPendente && (
          <div style={{ marginTop: '12px', background: '#FDF6E7', border: '1px solid #E4CDA0', borderRadius: 'var(--raio)', padding: '10px 12px' }}>
            <p style={{ fontSize: '12.5px', color: '#7A4E0B', margin: '0 0 8px', lineHeight: 1.5 }}>
              Muda o estado de <strong>{agsNaoFuturos.length} marcaç{agsNaoFuturos.length === 1 ? 'ão' : 'ões'}</strong> não futura{agsNaoFuturos.length === 1 ? '' : 's'} para <strong>{ESTADOS[estadoPendente]?.label}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={confirmarMudancaEstado}
                disabled={guardandoEstado}
                style={{ background: 'var(--cor-principal)', color: '#fff', border: 'none', borderRadius: 'var(--raio)', padding: '6px 14px', fontSize: '12.5px', cursor: guardandoEstado ? 'default' : 'pointer', opacity: guardandoEstado ? 0.6 : 1 }}
              >
                {guardandoEstado ? 'A aplicar…' : 'Confirmar'}
              </button>
              <button onClick={() => setEstadoPendente(null)} disabled={guardandoEstado} className="btn-secundario" style={{ padding: '6px 14px', fontSize: '12.5px' }}>
                Voltar atrás
              </button>
            </div>
          </div>
        )}
      </div>
    )

    const resumoFatConteudo = (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: isDesktop ? undefined : 1, minWidth: isDesktop ? undefined : '180px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--cor-texto-suave)', maxWidth: isDesktop ? 'none' : '280px' }}>
            <span>Sinais ({resumoFat.nS})</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(resumoFat.vS)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--cor-texto-suave)', maxWidth: isDesktop ? 'none' : '280px' }}>
            <span>Restantes ({resumoFat.nR})</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(resumoFat.vR)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '17px', fontWeight: '700', maxWidth: isDesktop ? 'none' : '280px', marginTop: isDesktop ? '6px' : 0, paddingTop: isDesktop ? '10px' : 0, borderTop: isDesktop ? '1px solid var(--cor-borda)' : 'none' }}>
            <span>Total do documento</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(totalFat)}</span>
          </div>
          {meiosFat.size > 1 && (
            <p style={{ fontSize: '11.5px', color: 'var(--cor-texto-suave)', margin: '4px 0 0', maxWidth: isDesktop ? 'none' : '280px' }}>
              Pagamento repartido: {[...meiosFat].map(m => m === METODO_STRIPE ? 'Stripe' : labelMetodo(m)).join(' + ')}.
            </p>
          )}
        </div>
        <button onClick={emitirFatura} disabled={emitindo || selFat.size === 0} className="btn-primario" style={{ opacity: (emitindo || selFat.size === 0) ? 0.5 : 1, whiteSpace: 'nowrap', width: isDesktop ? '100%' : undefined }}>
          {emitindo ? 'A emitir…' : 'Emitir fatura'}
        </button>
      </>
    )

    return (
      <div style={{ maxWidth: isDesktop ? '1100px' : '720px', margin: '0 auto', padding: isDesktop ? '24px 20px 64px' : '24px 20px 160px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={voltarAoDetalhe}
            aria-label="Voltar" title="Voltar"
            style={{ flexShrink: 0, marginTop: '2px', width: '34px', height: '34px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-borda)', background: 'var(--cor-creme)', color: 'var(--cor-principal)', fontSize: '18px', lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ←
          </button>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: '20px', margin: 0 }}>
              Emitir fatura ·{' '}
              <Link to={`/admin/clientes/${cliente.id}`} style={{ color: 'var(--cor-principal)', textDecoration: 'none' }}>
                {nomeCliente}
              </Link>
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', margin: '4px 0 0' }}>
              Escolhe o que entra no documento. Vem tudo o que já está pago e por faturar pré-escolhido; desmarca o que não quiseres incluir agora.
            </p>
          </div>
        </div>

        {cobranca && (
          <div style={{ background: '#E6F0E9', border: '1px solid #B7D3C0', color: '#166534', borderRadius: 'var(--raio)', padding: '12px 16px', marginBottom: '16px', fontWeight: 500, fontSize: '13.5px' }}>
            ✓ {cobranca.n} {cobranca.n === 1 ? 'parcela marcada' : 'parcelas marcadas'} como paga{cobranca.n === 1 ? '' : 's'}, no total de {fmtEur(cobranca.total)}.
          </div>
        )}

        {!vendusAtivo ? (
          <button onClick={voltarAoDetalhe} className="btn-primario">Concluir</button>
        ) : nadaParaFaturar ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--cor-texto-suave)', background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>✅</p>
            Não há pagamentos deste cliente por faturar.
            <div style={{ marginTop: '16px' }}>
              <button onClick={voltarAoDetalhe} className="btn-secundario">Voltar</button>
            </div>
          </div>
        ) : (
          <div style={isDesktop ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: '20px', alignItems: 'start' } : undefined}>
            <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 16px', borderBottom: '1px solid var(--cor-borda)' }}>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '600', color: 'var(--cor-texto-suave)' }}>
                  Marcações
                </span>
                <button onClick={toggleTodosFat} style={{ fontSize: '12.5px', color: 'var(--cor-principal)', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' }}>
                  Selecionar tudo o que falta
                </button>
              </div>

              {linhasFat.map(l => {
                const est = estadoAgendamentoFat(l)
                const incluido = est.todas || est.algumas
                const v = valoresAgendamento(l.ag, [
                  ...(l.sinal.id ? [{ agendamento_id: l.ag.id, tipo: 'sinal', valor: l.sinal.valor, pago: l.sinal.pago }] : []),
                  ...(l.restante.id ? [{ agendamento_id: l.ag.id, tipo: 'restante', valor: l.restante.valor, pago: l.restante.pago }] : []),
                ])
                return (
                  <div key={l.ag.id} style={{ display: 'flex', gap: '10px', padding: '10px 16px', borderBottom: '1px solid var(--cor-borda)', alignItems: 'flex-start', outline: incluido ? '2px solid var(--cor-principal)' : 'none', outlineOffset: '-1px' }}>
                    <input
                      type="checkbox"
                      checked={est.todas}
                      ref={el => { if (el) el.indeterminate = est.algumas }}
                      onChange={() => toggleAgendamentoFat(l)}
                      aria-label={`Incluir tudo o que falta faturar em ${l.ag.servicos?.nome || 'marcação'}`}
                      title="Liga/desliga as parcelas por faturar desta marcação"
                      style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: 'var(--cor-principal)', cursor: 'pointer', flex: 'none' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600' }}>{l.ag.servicos?.nome || 'Serviço'}</span>
                        <span style={{ fontSize: '11.5px', color: 'var(--cor-texto-suave)' }}>
                          {format(new Date(l.ag.data_hora_inicio), "d MMM yyyy · HH:mm", { locale: pt })}
                          {l.ag.profissionais?.nome && <> · {l.ag.profissionais.nome}</>}
                          <> · Total </><strong>{fmtEur(v.total)}</strong>
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '7px', flexWrap: 'wrap' }}>
                        <CaixaParcelaFatura l={l} tipo="sinal"
                          selecionavel={parcelasPorFaturar(l).includes('sinal')}
                          selecionada={selFat.has(l.sinal?.id)}
                          onSelecionar={() => toggleParcelaFat(l.sinal.id)} />
                        <CaixaParcelaFatura l={l} tipo="restante"
                          selecionavel={parcelasPorFaturar(l).includes('restante')}
                          selecionada={selFat.has(l.restante?.id)}
                          onSelecionar={() => toggleParcelaFat(l.restante.id)} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {isDesktop ? (
              <div style={{ position: 'sticky', top: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {resumoFatConteudo}
                </div>
                {estadoBox}
              </div>
            ) : (
              <>
                {/* No telemóvel o resumo fica fixo em baixo — a caixa de estado
                    vem antes, no fluxo normal, para não competir por espaço com
                    a barra fixa. */}
                {estadoBox}
                <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--cor-branco)', borderTop: '1px solid var(--cor-borda)', boxShadow: '0 -4px 18px rgba(0,0,0,.10)', padding: '12px 18px' }}>
                  <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    {resumoFatConteudo}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  // ─── Fase 1: selecionar & cobrar ──────────────────────────
  // Conteúdo do resumo (sinais/restantes/total + método + botão) — partilhado
  // entre a barra fixa (mobile) e o painel lateral fixo (desktop).
  const resumoConteudo = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: isDesktop ? undefined : 1, minWidth: isDesktop ? undefined : '180px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--cor-texto-suave)', maxWidth: isDesktop ? 'none' : '280px' }}>
          <span>Sinais ({resumo.nS})</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(resumo.vS)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--cor-texto-suave)', maxWidth: isDesktop ? 'none' : '280px' }}>
          <span>Restantes ({resumo.nR})</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(resumo.vR)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '17px', fontWeight: '700', maxWidth: isDesktop ? 'none' : '280px', marginTop: isDesktop ? '6px' : 0, paddingTop: isDesktop ? '10px' : 0, borderTop: isDesktop ? '1px solid var(--cor-borda)' : 'none' }}>
          <span>A marcar como pago</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEur(resumo.total)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
        <label htmlFor="metodo-cobranca">Método</label>
        <select id="metodo-cobranca" value={metodo} onChange={e => setMetodo(e.target.value)} style={valInput}>
          {metodosPagamento.map(m => <option key={m.chave} value={m.chave}>{m.label}</option>)}
        </select>
      </div>
      <button onClick={cobrar} disabled={enviando || resumo.total <= 0} className="btn-primario" style={{ opacity: (enviando || resumo.total <= 0) ? 0.5 : 1, whiteSpace: 'nowrap', width: isDesktop ? '100%' : undefined }}>
        {enviando ? 'A registar…' : 'Marcar como pago'}
      </button>
    </>
  )

  return (
    <div style={{ maxWidth: isDesktop ? '1100px' : '720px', margin: '0 auto', padding: isDesktop ? '24px 20px 64px' : '24px 20px 160px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label="Voltar" title="Voltar"
          style={{ flexShrink: 0, marginTop: '2px', width: '34px', height: '34px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-borda)', background: 'var(--cor-creme)', color: 'var(--cor-principal)', fontSize: '18px', lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: '20px', margin: 0 }}>
            Cobrar em conjunto ·{' '}
            <Link to={`/admin/clientes/${cliente.id}`} style={{ color: 'var(--cor-principal)', textDecoration: 'none' }}>
              {nomeCliente}
            </Link>
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', margin: '4px 0 0' }}>
            Os valores por pagar são editáveis.
          </p>
        </div>
      </div>

      {nadaParaCobrar ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--cor-texto-suave)', background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>✅</p>
          Este cliente não tem nada por saldar.
        </div>
      ) : (
        <div style={isDesktop ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: '20px', alignItems: 'start' } : undefined}>
          <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 16px', borderBottom: '1px solid var(--cor-borda)' }}>
              <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '600', color: 'var(--cor-texto-suave)' }}>
                Marcações
              </span>
              <button onClick={toggleTodos} style={{ fontSize: '12.5px', color: 'var(--cor-principal)', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' }}>
                Selecionar tudo o que falta
              </button>
            </div>

            {linhasVisiveis.map(l => {
              const est = estadoAgendamento(l)
              const incluido = est.todas || est.algumas
              const v = valoresAgendamento(l.ag, [
                ...(l.sinal.id ? [{ agendamento_id: l.ag.id, tipo: 'sinal', valor: l.sinal.valor, pago: l.sinal.pago }] : []),
                ...(l.restante.id ? [{ agendamento_id: l.ag.id, tipo: 'restante', valor: l.restante.valor, pago: l.restante.pago }] : []),
              ])
              return (
                <div key={l.ag.id} style={{ display: 'flex', gap: '10px', padding: '10px 16px', borderBottom: '1px solid var(--cor-borda)', alignItems: 'flex-start', outline: incluido ? '2px solid var(--cor-principal)' : 'none', outlineOffset: '-1px' }}>
                  <input
                    type="checkbox"
                    checked={est.todas}
                    ref={el => { if (el) el.indeterminate = est.algumas }}
                    disabled={tiposCobraveis(l).length === 0}
                    onChange={() => toggleAgendamento(l)}
                    aria-label={`Incluir tudo o que falta em ${l.ag.servicos?.nome || 'marcação'}`}
                    title="Liga/desliga as duas parcelas desta marcação"
                    style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: 'var(--cor-principal)', cursor: tiposCobraveis(l).length === 0 ? 'default' : 'pointer', flex: 'none' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>{l.ag.servicos?.nome || 'Serviço'}</span>
                      <span style={{ fontSize: '11.5px', color: 'var(--cor-texto-suave)' }}>
                        {format(new Date(l.ag.data_hora_inicio), "d MMM yyyy · HH:mm", { locale: pt })}
                        {l.ag.profissionais?.nome && <> · {l.ag.profissionais.nome}</>}
                        <> · Total </><strong>{fmtEur(v.total)}</strong>
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '7px', flexWrap: 'wrap' }}>
                      <CaixaParcela l={l} tipo="sinal" cobravel={sinalCobravel(l)}
                        selecionada={sel.has(chaveParcela(l.ag.id, 'sinal'))}
                        onSelecionar={() => toggleParcela(l.ag.id, 'sinal')}
                        valor={valorParcela(l.ag.id, 'sinal', l.sinal)} onValor={x => setValorParcela(l.ag.id, 'sinal', x)} />
                      <CaixaParcela l={l} tipo="restante" cobravel={restanteCobravel(l)}
                        selecionada={sel.has(chaveParcela(l.ag.id, 'restante'))}
                        onSelecionar={() => toggleParcela(l.ag.id, 'restante')}
                        valor={valorParcela(l.ag.id, 'restante', l.restante)} onValor={x => setValorParcela(l.ag.id, 'restante', x)} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {isDesktop ? (
            /* Painel de resumo fixo (desktop) — substitui a barra do fundo, sem
               desperdiçar a largura livre ao lado da lista. */
            <div style={{ position: 'sticky', top: '20px', background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {resumoConteudo}
            </div>
          ) : (
            /* Barra de resumo sticky (mobile) */
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--cor-branco)', borderTop: '1px solid var(--cor-borda)', boxShadow: '0 -4px 18px rgba(0,0,0,.10)', padding: '12px 18px' }}>
              <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {resumoConteudo}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Caixa Sinal/Restante do modo "emitir fatura" — mesmo layout da CaixaParcela,
// mas sempre em leitura: a parcela já está paga, o valor não se edita aqui.
// Só tem checkbox quando é candidata (paga e ainda por faturar).
function CaixaParcelaFatura({ l, tipo, selecionavel, selecionada, onSelecionar }) {
  const p = l[tipo]
  const cap = tipo === 'sinal' ? 'Sinal' : 'Restante'

  let estadoTxt, estadoCor
  if (!p?.pago) {
    estadoTxt = '—'; estadoCor = 'var(--cor-texto-suave)'
  } else if (p.faturado) {
    estadoTxt = 'já faturado'; estadoCor = 'var(--cor-texto-suave)'
  } else {
    estadoTxt = `pago · ${p.origem === 'stripe' ? 'Stripe (online)' : labelMetodo(p.metodo)}`
    estadoCor = '#166534'
  }

  const ativa = selecionavel && selecionada
  return (
    <div style={{ flex: 1, minWidth: '160px', border: `1px solid ${ativa ? 'var(--cor-principal)' : 'var(--cor-borda)'}`, borderRadius: 'var(--raio)', padding: '6px 10px', background: ativa ? '#FBF6EF' : 'var(--cor-creme)', boxShadow: ativa ? 'inset 0 0 0 1px var(--cor-principal)' : 'none', opacity: p.pago ? 1 : 0.55 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--cor-texto-suave)', fontWeight: '600', cursor: selecionavel ? 'pointer' : 'default' }}>
          {selecionavel && (
            <input
              type="checkbox"
              checked={!!selecionada}
              onChange={onSelecionar}
              aria-label={`Faturar ${cap.toLowerCase()}`}
              style={{ width: '15px', height: '15px', margin: 0, accentColor: 'var(--cor-principal)', cursor: 'pointer' }}
            />
          )}
          {cap}{p.pago && ' 🔒'}
        </label>
        <span style={{ fontSize: '10.5px', fontWeight: '600', color: estadoCor, textAlign: 'right' }}>{estadoTxt}</span>
      </div>
      <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--cor-texto)', fontVariantNumeric: 'tabular-nums' }}>
        {fmtEur(p.valor)}
      </div>
    </div>
  )
}

// Caixa Sinal/Restante compacta: título + estado numa só linha (poupa altura);
// input editável quando por pagar; leitura quando já pago / não aplicável.
function CaixaParcela({ l, tipo, cobravel, valor, onValor, selecionada, onSelecionar }) {
  const p = l[tipo]
  const cap = tipo === 'sinal' ? 'Sinal' : 'Restante'
  // O restante de uma marcação futura é cobrável (o cliente pode deixar tudo
  // pago), mas não vem escolhido — daí o aviso em vez de um bloqueio.
  const restanteAdiantado = tipo === 'restante' && l.futuro && !p.pago

  // Estado como texto colorido inline (ao lado do título), não como pill à parte.
  let estadoTxt, estadoCor
  if (p.pago) {
    estadoTxt = p.faturado
      ? `pago · faturado${p.metodo ? ` · ${labelMetodo(p.metodo)}` : ''}`
      : `pago${p.metodo ? ` · ${labelMetodo(p.metodo)}` : ''} · por faturar`
    estadoCor = '#166534'
  } else if (restanteAdiantado) {
    estadoTxt = 'adiantado'; estadoCor = 'var(--cor-texto-suave)'
  } else if (cobravel) {
    estadoTxt = 'por pagar'; estadoCor = '#B45309'
  } else {
    estadoTxt = '—'; estadoCor = 'var(--cor-texto-suave)'
  }

  const editavel = cobravel && !p.pago
  // Só as parcelas por cobrar têm caixa de seleção; as pagas (ou o restante de
  // uma marcação futura) ficam só de leitura.
  const selecionavel = cobravel && !p.pago
  const ativa = selecionavel && selecionada
  return (
    <div style={{ flex: 1, minWidth: '160px', border: `1px solid ${ativa ? 'var(--cor-principal)' : 'var(--cor-borda)'}`, borderRadius: 'var(--raio)', padding: '6px 10px', background: ativa ? '#FBF6EF' : 'var(--cor-creme)', boxShadow: ativa ? 'inset 0 0 0 1px var(--cor-principal)' : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--cor-texto-suave)', fontWeight: '600', cursor: selecionavel ? 'pointer' : 'default' }}>
          {selecionavel && (
            <input
              type="checkbox"
              checked={!!selecionada}
              onChange={onSelecionar}
              aria-label={`Cobrar ${cap.toLowerCase()}`}
              style={{ width: '15px', height: '15px', margin: 0, accentColor: 'var(--cor-principal)', cursor: 'pointer' }}
            />
          )}
          {cap}{p.pago && ' 🔒'}
        </label>
        <span style={{ fontSize: '10.5px', fontWeight: '600', color: estadoCor, textAlign: 'right' }}>{estadoTxt}</span>
      </div>
      {editavel ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            type="number" step="0.01" min="0" value={valor}
            onChange={e => onValor(e.target.value)}
            aria-label={`${cap} de ${l.ag.servicos?.nome || 'marcação'}`}
            style={{ width: '100%', fontSize: '15px', fontWeight: '700', fontFamily: 'inherit', color: 'var(--cor-texto)', border: '1px solid var(--cor-borda)', borderRadius: '6px', padding: '4px 8px', background: 'var(--cor-branco)', fontVariantNumeric: 'tabular-nums' }}
          />
          <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>€</span>
        </div>
      ) : (
        <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--cor-texto)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtEur(p.valor)}
        </div>
      )}
    </div>
  )
}
